import {
  type RefractoryChemicalDataFilter,
  type RefractoryDataRange,
  type RefractoryPhysicalDataFilter
} from '@domain/refractory-product-catalog'
import {Button, Modal, ModalFooter, ModalTitle, Tab, TabsPanel} from '@hm/fundamental'
import {zodResolver} from '@hookform/resolvers/zod'
import {mdiCheck, mdiClose} from '@mdi/js'
import {useMemo, useState} from 'react'
import {useForm, useWatch} from 'react-hook-form'
import {useTranslation} from 'react-i18next'
import {z} from 'zod'

import {FEATURE_NAMESPACE} from '../../consts'

import {
  type AdvancedFiltersFormValues,
  type DraftRange,
  compounds,
  physicalFilterFields
} from './RefractoryAdvancedFilters.types'
import {RefractoryChemicalDataTab} from './RefractoryChemicalDataTab'
import {RefractoryPhysicalDataTab} from './RefractoryPhysicalDataTab'

interface AdvancedFilters {
  chemicalData: RefractoryChemicalDataFilter[]
  csRatio?: RefractoryDataRange
  physicalData?: RefractoryPhysicalDataFilter
}

interface RefractoryAdvancedFiltersModalProps extends AdvancedFilters {
  onApply: (filters: AdvancedFilters) => void
  onClose: () => void
}

const hasRange = (range?: RefractoryDataRange): boolean =>
  range?.min !== undefined || range?.max !== undefined

export const getAdvancedFilterCount = ({
  chemicalData,
  csRatio,
  physicalData
}: AdvancedFilters): number =>
  chemicalData.filter(hasRange).length +
  Number(hasRange(csRatio)) +
  physicalFilterFields.filter(({name}) => hasRange(physicalData?.[name])).length

const toDraftRange = (range?: RefractoryDataRange): DraftRange => ({
  min: range?.min?.toString() ?? '',
  max: range?.max?.toString() ?? ''
})

const toRange = ({min, max}: DraftRange): RefractoryDataRange => ({
  ...(min.trim() !== '' && {min: Number(min)}),
  ...(max.trim() !== '' && {max: Number(max)})
})

export const RefractoryAdvancedFiltersModal: React.FC<RefractoryAdvancedFiltersModalProps> = ({
  chemicalData,
  csRatio,
  physicalData,
  onApply,
  onClose
}) => {
  const {t} = useTranslation(FEATURE_NAMESPACE)
  const [activeTab, setActiveTab] = useState('chemical')
  const schema = useMemo(() => {
    const range = (maxValue?: number) => {
      const bound = z
        .string()
        .refine(
          (value) =>
            value.trim() === '' ||
            (Number.isFinite(Number(value)) &&
              Number(value) >= 0 &&
              (maxValue === undefined || Number(value) <= maxValue)),
          t(
            maxValue === undefined
              ? 'refraCatalog.filters.physicalRangeError'
              : 'refraCatalog.filters.rangeError'
          )
        )
      return z.object({min: bound, max: bound}).superRefine(({min, max}, context) => {
        if (min.trim() !== '' && max.trim() !== '' && Number(min) > Number(max)) {
          context.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['max'],
            message: t('refraCatalog.filters.rangeOrderError')
          })
        }
      })
    }
    return z.object({
      chemicalData: z.array(range(100)),
      csRatio: range(100),
      measurementSystem: z.union([z.literal('Metric'), z.literal('US')]),
      physicalData: z.object({
        bulkDensity: range(),
        porosity: range(100),
        coldCrushingStrength: range(),
        abrasionResistance: range(),
        shelfLife: range()
      })
    })
  }, [t])
  const {
    control,
    handleSubmit,
    setValue,
    trigger,
    formState: {isValid, isDirty, dirtyFields}
  } = useForm<AdvancedFiltersFormValues>({
    resolver: zodResolver(schema),
    mode: 'onChange',
    defaultValues: {
      chemicalData: compounds.map((compound) =>
        toDraftRange(chemicalData.find((filter) => filter.compound === compound))
      ),
      csRatio: toDraftRange(csRatio),
      measurementSystem: physicalData?.measurementSystem ?? 'US',
      physicalData: {
        bulkDensity: toDraftRange(physicalData?.bulkDensity),
        porosity: toDraftRange(physicalData?.porosity),
        coldCrushingStrength: toDraftRange(physicalData?.coldCrushingStrength),
        abrasionResistance: toDraftRange(physicalData?.abrasionResistance),
        shelfLife: toDraftRange(physicalData?.shelfLife)
      }
    }
  })
  const draft = useWatch({control})
  const count =
    (draft.chemicalData?.filter((range) => range.min?.trim() || range.max?.trim()).length ?? 0) +
    Number(Boolean(draft.csRatio?.min?.trim() || draft.csRatio?.max?.trim()))
  const physicalCount = physicalFilterFields.filter(({name}) => {
    const range = draft.physicalData?.[name]
    return range?.min?.trim() || range?.max?.trim()
  }).length
  const hasFilterChanges = Boolean(
    dirtyFields.chemicalData ||
    dirtyFields.csRatio ||
    dirtyFields.physicalData ||
    (dirtyFields.measurementSystem && physicalCount > 0)
  )

  const onSubmit = (values: AdvancedFiltersFormValues): void => {
    const nextChemicalData = compounds.flatMap((compound, index) => {
      const range = toRange(values.chemicalData[index])
      return hasRange(range) ? [{compound, ...range}] : []
    })
    const nextCsRatio = toRange(values.csRatio)
    const nextPhysicalData: RefractoryPhysicalDataFilter = {
      measurementSystem: values.measurementSystem
    }
    physicalFilterFields.forEach(({name}) => {
      const range = toRange(values.physicalData[name])
      if (hasRange(range)) nextPhysicalData[name] = range
    })
    onApply({
      chemicalData: nextChemicalData,
      csRatio: hasRange(nextCsRatio) ? nextCsRatio : undefined,
      physicalData: physicalCount > 0 ? nextPhysicalData : undefined
    })
  }

  return (
    <Modal
      open
      onClose={onClose}
      width="wide"
      className="!w-[636px] [&>div]:p-0"
      data-cy="refra-catalog-advanced-filters-modal"
    >
      <form
        onSubmit={handleSubmit(onSubmit)}
        role="dialog"
        aria-modal="true"
        aria-labelledby="refra-catalog-advanced-filters-title"
        className="flex max-h-[calc(100dvh-2rem)] flex-col gap-4 rounded-lg bg-container_background py-4 text-foreground"
      >
        <ModalTitle className="flex min-h-12 shrink-0 items-center !px-4 !pt-0">
          <span id="refra-catalog-advanced-filters-title">
            {t('refraCatalog.filters.advanced')}
          </span>
        </ModalTitle>
        <TabsPanel onSelect={setActiveTab} className="shrink-0">
          <Tab
            value="chemical"
            label={`${t('refraCatalog.filters.chemicalData')} (${count})`}
            isActive={activeTab === 'chemical'}
            data-cy="refra-catalog-chemical-tab"
          />
          <Tab
            value="physical"
            label={`${t('refraCatalog.filters.physicalData')} (${physicalCount})`}
            isActive={activeTab === 'physical'}
            data-cy="refra-catalog-physical-tab"
          />
        </TabsPanel>
        <div className="min-h-0 flex-1 overflow-y-auto px-6 pb-6">
          <RefractoryChemicalDataTab
            control={control}
            hidden={activeTab !== 'chemical'}
            setValue={setValue}
            trigger={trigger}
          />
          <RefractoryPhysicalDataTab
            control={control}
            hidden={activeTab !== 'physical'}
            setValue={setValue}
            trigger={trigger}
          />
        </div>
        <ModalFooter className="shrink-0 flex-wrap !px-4 !py-0">
          <Button
            type="button"
            variant="secondary"
            size="large"
            icon={mdiClose}
            onClick={onClose}
            data-cy="refra-catalog-close-advanced-filters"
          >
            {t('common.close')}
          </Button>
          <Button
            type="submit"
            size="large"
            icon={mdiCheck}
            disabled={!isValid || !isDirty || !hasFilterChanges}
            data-cy="refra-catalog-apply-advanced-filters"
          >
            {t('refraCatalog.filters.apply')}
          </Button>
        </ModalFooter>
      </form>
    </Modal>
  )
}
